import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  formats,
  messages,
  translations,
} from '../../../programacaoAgendamento/components/schedule-calendar/calendar-utils'
import { ScheduleEditor } from '../create-schedule'
import { GlobalContext } from '../../../../../App'
import { getdoc, metodoPrograma } from '../../../../../components/api'
import { AgendaContext } from '../..'
import { Calendar, dayjsLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'
import './calendarOverrides.css'
import dayjs from 'dayjs'
import { EventRender } from './EventRender'
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import { MdClose } from 'react-icons/md'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { ZoomIn, ZoomOut, EventBusy } from '@mui/icons-material'
import 'dayjs/locale/pt-br'

dayjs.locale('pt-br')

const localizer = dayjsLocalizer(dayjs)

const DragAndDropCalendar = withDragAndDrop(Calendar)

const LARGURA_MINIMA_INICIAL = 60
const LARGURA_MAXIMA_COLUNA = 280
const PASSO_LARGURA_COLUNA = 20
const LARGURA_PADRAO_COLUNA = 180
const CHAVE_LARGURA_COLUNA = 'agenda-checkin-largura-coluna'

const CalendarToolbar = ({
  date,
  label,
  onNavigate,
  larguraColuna,
  larguraMinima,
  centroCalendario,
  diminuirLargura,
  aumentarLargura,
}) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const fecharCalendario = () => {
    anchorEl?.blur()
    setAnchorEl(null)
  }

  const selecionarData = (novaData) => {
    if (!novaData) return

    onNavigate('DATE', novaData.toDate())
    fecharCalendario()
  }

  return (
    <>
      <div
        className="rbc-toolbar calendar-toolbar"
        style={{ width: centroCalendario ? centroCalendario * 2 : '100%' }}
      >
        <span className="rbc-btn-group">
          <button type="button" onClick={() => onNavigate('TODAY')}>
            Hoje
          </button>
          <button type="button" onClick={() => onNavigate('PREV')}>
            {'<'}
          </button>
          <button type="button" onClick={() => onNavigate('NEXT')}>
            {'>'}
          </button>
        </span>

        <button
          type="button"
          className="rbc-toolbar-label calendar-date-button"
          aria-label="Escolher uma data"
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchorEl)}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {label}
        </button>

        <span
          className="rbc-btn-group calendar-density-controls"
          aria-label="Ajustar largura da agenda"
        >
          <button
            type="button"
            title="Diminuir largura para exibir mais pacientes"
            aria-label="Diminuir largura para exibir mais pacientes"
            disabled={larguraColuna <= larguraMinima}
            onClick={diminuirLargura}
          >
            <ZoomOut fontSize="small" />
          </button>
          <button
            type="button"
            title="Aumentar largura para facilitar a leitura"
            aria-label="Aumentar largura para facilitar a leitura"
            disabled={larguraColuna >= LARGURA_MAXIMA_COLUNA}
            onClick={aumentarLargura}
          >
            <ZoomIn fontSize="small" />
          </button>
        </span>
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={fecharCalendario}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
          <DateCalendar
            views={['year', 'month', 'day']}
            openTo="day"
            value={dayjs(date)}
            onChange={selecionarData}
          />
        </LocalizationProvider>
      </Popover>
    </>
  )
}

const ResourceHeader = ({ resource, onPatientClick }) => {
  const possuiAlergia = Boolean(
    resource.dsAlergia?.trim() || resource.dsRestricaoAlimentar?.trim(),
  )
  const pagouCheckin = resource.pagouCheckin === true
  const [proposta, ...partesNome] = resource.name?.split(' - ') || []
  const nomePaciente = partesNome.join(' - ') || resource.name

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: '8px',
        height: '80px',
        backgroundColor: '#f6f6f6',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 20,
        }}
      >
        <span
          style={{
            backgroundColor: '#e0e0e0',
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
            padding: '3px 5px',
          }}
        >
          {proposta}
        </span>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <FaCheckCircle
            aria-label={
              pagouCheckin
                ? 'Pagamento do check-in confirmado'
                : 'Pagamento do check-in pendente'
            }
            title={
              pagouCheckin
                ? 'Pagamento do check-in confirmado'
                : 'Pagamento do check-in pendente'
            }
            color={pagouCheckin ? '#2e7d32' : '#b0b0b0'}
            size={16}
            style={{ opacity: pagouCheckin ? 1 : 0.45 }}
          />
          {possuiAlergia && (
            <FaExclamationTriangle
              aria-label="Alergia medicamentosa ou alimentar"
              title="Alergia medicamentosa ou alimentar"
              color="#d32f2f"
              size={16}
            />
          )}
        </span>
      </div>
      <Tooltip title={nomePaciente} enterDelay={500}>
        <button
          type="button"
          onClick={() => onPatientClick(resource)}
          style={{
            appearance: 'none',
            background: 'none',
            border: 0,
            color: 'inherit',
            cursor: 'pointer',
            display: '-webkit-box',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.25,
            marginTop: 5,
            overflow: 'hidden',
            padding: 0,
            textAlign: 'center',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            width: '100%',
          }}
        >
          {nomePaciente}
        </button>
      </Tooltip>
    </div>
  )
}

export const ConsultsList = ({ listaAgendamentos, consultaAgenda }) => {
  const { terapias, consults, dispatch, agendamentoSelecionado, selectedDay } =
    useContext(AgendaContext)
  const { agenda, usuarioLogado } = useContext(GlobalContext)
  const permiteMoverAgendamentos = Number(usuarioLogado?.id) !== 45
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null)
  const [abaDiagnostico, setAbaDiagnostico] = useState(0)
  const [imagemPacienteUrl, setImagemPacienteUrl] = useState('')
  const [imagemCarregando, setImagemCarregando] = useState(false)
  const calendarContainerRef = useRef(null)
  const [larguraMinima, setLarguraMinima] = useState(LARGURA_MINIMA_INICIAL)
  const [centroCalendario, setCentroCalendario] = useState(0)
  const [larguraColuna, setLarguraColuna] = useState(() => {
    const larguraSalva = Number(localStorage.getItem(CHAVE_LARGURA_COLUNA))

    return larguraSalva >= LARGURA_MINIMA_INICIAL &&
      larguraSalva <= LARGURA_MAXIMA_COLUNA
      ? larguraSalva
      : LARGURA_PADRAO_COLUNA
  })

  const events = useMemo(
    () =>
      consults?.map((item) => ({
        id: item[0],
        title: item[5],
        start: new Date(item[1]),
        end: new Date(item[2]),
        data: item,
        resourceId: item[3],
      })),
    [consults],
  )
  const nrTempoAgenda = 30

  const buscaDadosMedico = useCallback(
    (cdMedico) => {
      metodoPrograma('buscaDadosMedico', { cdMedico }).then((res) => {
        if (res.status) {
          dispatch({ field: 'medico', value: res })
        }
      })
    },
    [dispatch],
  )

  const onSelectEvent = (event) => {
    dispatch({
      field: 'agendamentoSelecionado',
      value: {
        data: event.data,
        modal: true,
        start: event.start,
        end: event.end,
        resourceId: event.resourceId,
        name: event.name,
      },
    })
  }

  const onSlotSelect = (slot) => {
    dispatch({
      field: 'agendamentoSelecionado',
      value: {
        start: slot.start,
        end: slot.end,
        modal: true,
        resourceId: slot.resourceId,
        name: slot.name,
      },
    })
  }

  const onNavigate = (date) => {
    dispatch({ field: 'selectedDay', value: date })
  }

  const alterarLarguraColuna = useCallback(
    (alteracao) => {
      setLarguraColuna((larguraAtual) => {
        const novaLargura = Math.min(
          LARGURA_MAXIMA_COLUNA,
          Math.max(larguraMinima, larguraAtual + alteracao),
        )

        localStorage.setItem(CHAVE_LARGURA_COLUNA, String(novaLargura))
        return novaLargura
      })
    },
    [larguraMinima],
  )

  useEffect(() => {
    const container = calendarContainerRef.current
    const quantidadeColunas = terapias?.length || 0

    if (!container || !quantidadeColunas) return undefined

    const atualizarLarguraMinima = () => {
      setLarguraMinima(LARGURA_MINIMA_INICIAL)
      setCentroCalendario(container.clientWidth / 2)
    }

    atualizarLarguraMinima()
    const resizeObserver = new ResizeObserver(atualizarLarguraMinima)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [terapias?.length])

  const renderToolbar = useCallback(
    (props) => (
      <CalendarToolbar
        {...props}
        larguraColuna={larguraColuna}
        larguraMinima={larguraMinima}
        centroCalendario={centroCalendario}
        diminuirLargura={() => alterarLarguraColuna(-PASSO_LARGURA_COLUNA)}
        aumentarLargura={() => alterarLarguraColuna(PASSO_LARGURA_COLUNA)}
      />
    ),
    [alterarLarguraColuna, centroCalendario, larguraColuna, larguraMinima],
  )

  const abrirInformacoesPaciente = (paciente) => {
    setPacienteSelecionado(paciente)
    setAbaDiagnostico(0)
  }

  const fecharInformacoesPaciente = () => setPacienteSelecionado(null)

  const nomePaciente = pacienteSelecionado?.name?.replace(/^\d+\s*-\s*/, '')
  const dataNascimento = pacienteSelecionado?.dtNascimento
    ? dayjs(pacienteSelecionado.dtNascimento)
    : null
  const dataNascimentoFormatada = dataNascimento?.isValid()
    ? dataNascimento.format('DD/MM/YYYY')
    : 'Não informado'
  const idadePaciente = dataNascimento?.isValid()
    ? `${dayjs().diff(dataNascimento, 'year')} anos`
    : 'Não informada'
  const alergiaMedicamento =
    pacienteSelecionado?.dsAlergia || 'Nenhuma alergia informada'
  const restricaoAlimentar =
    pacienteSelecionado?.dsRestricaoAlimentar ||
    'Nenhuma restrição alimentar informada'
  const diagnosticosPaciente = [
    { titulo: 'Nutricionista', dados: pacienteSelecionado?.diagNutri },
    { titulo: 'Biólogo', dados: pacienteSelecionado?.diagBio },
    {
      titulo: 'Médico Diretor Clínico',
      dados: pacienteSelecionado?.diagClinico,
    },
  ]
  const diagnosticoAtual = diagnosticosPaciente[abaDiagnostico]
  const avaliacoesAtual = Array.isArray(diagnosticoAtual.dados)
    ? diagnosticoAtual.dados
    : []
  const agendaSemRegistros =
    consultaAgenda.concluida &&
    !consultaAgenda.carregando &&
    (terapias?.length || 0) === 0

  useEffect(() => {
    const tokenImagem = pacienteSelecionado?.imagem?.[1]
    let imagemUrl

    if (!tokenImagem) {
      setImagemPacienteUrl('')
      setImagemCarregando(false)
      return undefined
    }

    const carregarImagem = async () => {
      setImagemCarregando(true)
      setImagemPacienteUrl('')

      try {
        const response = await getdoc(tokenImagem)
        if (typeof response.blob !== 'function') return

        const blob = await response.blob()
        imagemUrl = URL.createObjectURL(blob)
        setImagemPacienteUrl(imagemUrl)
      } catch (error) {
        console.error('Erro ao carregar imagem do paciente:', error)
      } finally {
        setImagemCarregando(false)
      }
    }

    carregarImagem()

    return () => {
      if (imagemUrl) URL.revokeObjectURL(imagemUrl)
    }
  }, [pacienteSelecionado?.imagem])

  useEffect(() => {
    if (agenda !== null) {
      buscaDadosMedico(agenda[2])
    }
    if (agendamentoSelecionado.modal === false) {
      dispatch({
        field: 'agendamentoSelecionado',
        value: { data: null, modal: false },
      })
    }
  }, [agenda, buscaDadosMedico, dispatch, agendamentoSelecionado.modal])

  const onEventDrop = async ({ event, start, end, resourceId }) => {
    if (!permiteMoverAgendamentos) return

    const partes = resourceId.split('-')

    const obj = {
      idAgendamento: event.id,
      idAtendimento: partes[0],
      idPaciente: partes[1],
      idAgenda: event?.data?.[4],
      dtInicio: dayjs(start).format('YYYY-MM-DD HH:mm'),
      dtFim: dayjs(end).format('YYYY-MM-DD HH:mm'),
    }
    dispatch({
      type: 'setConsults',
      value: consults.map((value) => {
        const item = [...value]
        if (item[0] === event.id) {
          item[1] = start
          item[2] = end
          item[16] = resourceId
        }
        return item
      }),
    })
    await metodoPrograma('trocaPosicaoAgendamento', obj).then((res) => {
      if (res.status) {
        listaAgendamentos()
      }
    })
  }

  return (
    <>
      <div className="calendar-container agenda-checkin-calendar">
        {agendaSemRegistros && (
          <Box className="calendar-empty-state" role="status">
            <EventBusy sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography variant="h6" component="p">
              Nenhum registro encontrado para{' '}
              {dayjs(selectedDay).format('DD/MM/YYYY')}.
            </Typography>
          </Box>
        )}
        <div ref={calendarContainerRef} className="calendar-horizontal-scroll">
          <div
            style={{
              minWidth: `${(terapias?.length || 0) * larguraColuna + 80}px`,
            }}
          >
            <DragAndDropCalendar
              localizer={localizer}
              messages={messages}
              formats={formats}
              translations={translations}
              onSelectEvent={onSelectEvent}
              selectable
              onEventDrop={onEventDrop}
              draggableAccessor={() => permiteMoverAgendamentos}
              onSelectSlot={onSlotSelect}
              components={{
                toolbar: renderToolbar,
                event: ({ event }) => <EventRender event={event} />,
                resourceHeader: (props) => (
                  <ResourceHeader
                    {...props}
                    onPatientClick={abrirInformacoesPaciente}
                  />
                ),
              }}
              views={['day']}
              eventPropGetter={() => ({
                style: {
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  border: 'none',
                  color: 'transparent',
                  fontSize: '4px',
                },
              })}
              slotPropGetter={() => ({
                style: {
                  backgroundColor: 'white',
                  width: '100%',
                  minHeight: '90px',
                  cursor: 'pointer',
                  fontSize: '13px',
                },
              })}
              onNavigate={onNavigate}
              min={dayjs().hour(7).minute(0)}
              max={dayjs().hour(19).minute(0)}
              step={nrTempoAgenda}
              timeslots={1}
              startAccessor="start"
              endAccessor="end"
              defaultView="day"
              resources={terapias || []}
              resourceIdAccessor="id"
              resourceTitleAccessor="name"
              events={events}
              hourFormat="24"
              style={{ height: 'calc(100vh - 50px)', marginTop: 0 }}
            />
          </div>
        </div>
      </div>
      {agendamentoSelecionado.modal && (
        <ScheduleEditor listaAgendamentos={listaAgendamentos} />
      )}
      <Dialog
        open={Boolean(pacienteSelecionado)}
        onClose={fecharInformacoesPaciente}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: '90vw' } }}
      >
        <DialogTitle sx={{ pr: 7 }}>
          Informações do paciente
          <IconButton
            aria-label="Fechar informações do paciente"
            onClick={fecharInformacoesPaciente}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Avatar
              alt={nomePaciente}
              src={imagemPacienteUrl}
              sx={{ width: 120, height: 120 }}
            >
              {imagemCarregando ? (
                <CircularProgress size={30} />
              ) : (
                nomePaciente?.charAt(0)
              )}
            </Avatar>
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Paciente:</strong> {nomePaciente}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Proposta:</strong> {pacienteSelecionado?.idAtendimento}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Data de nascimento:</strong> {dataNascimentoFormatada}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Idade:</strong> {idadePaciente}
              </Typography>
              <Typography
                variant="body2"
                color={
                  pacienteSelecionado?.dsAlergia
                    ? 'error.main'
                    : 'text.secondary'
                }
                sx={{ mt: 1 }}
              >
                <strong>Alergia medicamentosa:</strong> {alergiaMedicamento}
              </Typography>
              <Typography
                variant="body2"
                color={
                  pacienteSelecionado?.dsRestricaoAlimentar
                    ? 'error.main'
                    : 'text.secondary'
                }
              >
                <strong>Alergia alimentar:</strong> {restricaoAlimentar}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
            <Tabs
              value={abaDiagnostico}
              onChange={(_, novaAba) => setAbaDiagnostico(novaAba)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {diagnosticosPaciente.map((diagnostico) => {
                const quantidadeAvaliacoes = Array.isArray(diagnostico.dados)
                  ? diagnostico.dados.length
                  : 0

                return (
                  <Tab
                    key={diagnostico.titulo}
                    label={
                      <Badge
                        color="success"
                        invisible={!quantidadeAvaliacoes}
                        variant="dot"
                      >
                        <span>{diagnostico.titulo}</span>
                      </Badge>
                    }
                  />
                )
              })}
            </Tabs>
          </Box>
          {avaliacoesAtual.length ? (
            avaliacoesAtual.map((avaliacao) => {
              const [, dataHora, , profissional, descricao] = avaliacao

              return (
                <TextField
                  key={avaliacao[0]}
                  fullWidth
                  multiline
                  minRows={5}
                  label={`${profissional || 'Profissional não informado'} — ${
                    dataHora || 'Data não informada'
                  }`}
                  value={descricao || 'Sem descrição'}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 2 }}
                />
              )
            })
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={5}
              label={`Diagnóstico — ${diagnosticoAtual.titulo}`}
              value="Nenhum diagnóstico registrado."
              disabled
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharInformacoesPaciente}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
